import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { useEffect } from "react";
import { clearCart } from "@/lib/store";
import { trackEvent } from "@/lib/analytics";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getOrderForCurrentBuyer } from "@/lib/store.functions";
import { useAuth } from "@/hooks/useAuth";

const SearchSchema = z.object({
  order_id: z.string().uuid().optional(),
  session_id: z.string().optional(),
});

export const Route = createFileRoute("/store/checkout/success")({
  validateSearch: (s) => SearchSchema.parse(s),
  component: SuccessPage,
});

function SuccessPage() {
  const { order_id } = useSearch({ from: "/store/checkout/success" });
  const { user, loading: authLoading } = useAuth();
  const fetchOrder = useServerFn(getOrderForCurrentBuyer);
  const { data } = useQuery({
    queryKey: ["store", "order", order_id],
    queryFn: () => fetchOrder({ data: { orderId: order_id! } }),
    enabled: !!order_id && !!user && !authLoading,
    retry: false,
    refetchInterval: (q) =>
      q.state.data?.order?.status === "paid" ? false : 3000,
  });
  useEffect(() => {
    clearCart();
    trackEvent("library_purchase", order_id ? { order_id } : undefined);
  }, [order_id]);

  const needsAgreement = !!data?.agreementRequired && !data?.agreementSigned;

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />
      <section className="mx-auto max-w-2xl px-6 py-24 text-center">
        <Eyebrow align="center">Payment received</Eyebrow>
        {needsAgreement ? (
          <>
            <h1 className="mt-6 font-serif text-4xl text-navy-900">
              One final step before access
            </h1>
            <p className="mt-6 font-sans text-lg text-charcoal-900">
              This purchase includes membership in the Currency Thoughts private
              study association, so we need your signature on the Personal
              Member Agreement. It takes about thirty seconds, and your access
              unlocks immediately afterward.
            </p>
            {order_id && (
              <div className="mt-10">
                <Link
                  to="/store/agreement/$orderId"
                  params={{ orderId: order_id }}
                >
                  <PrimaryButton>Sign the agreement</PrimaryButton>
                </Link>
              </div>
            )}
          </>
        ) : (
          <>
            <h1 className="mt-6 font-serif text-4xl text-navy-900">
              Your library is ready
            </h1>
            <p className="mt-6 font-sans text-lg text-charcoal-900">
              Thank you. Your materials are in your library now, and a
              confirmation email is on its way. All digital sales are final.
            </p>
            <div className="mt-10">
              <Link to="/store/library">
                <PrimaryButton>Go to my library</PrimaryButton>
              </Link>
            </div>
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}